import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { leaveCreditApi, leaveRecordApi, leaveTypeApi } from '../../leave/leaveApi';
import { LeaveCreditKind, type LeaveCreditBalance, type LeaveRecord, type LeaveType } from '../../leave/leaveTypes';
import { sampleLeaveRecord } from '../../leave/utils/sampleLeaveRecord';
import { LeaveBalanceFigure } from '../../leave/components/LeaveBalanceFigure';
import { remainingBalance } from '../../leave/leaveBalance';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';

type Deduction = '' | 'deduct' | 'record';

/**
 * Weekdays from start to end inclusive — the SUGGESTED days, never demanded. Holidays and half
 * days are the application's to count, so the reader overrides it by typing.
 *
 * Walked on the calendar date's own parts at noon UTC, so no timezone can move a day.
 */
function weekdaysBetween(start: string, end: string): number | null {
  if (!start || !end || end < start) return null;

  const day = new Date(`${start}T12:00:00Z`);
  const last = new Date(`${end}T12:00:00Z`);
  let count = 0;

  // Capped: a mistyped year would otherwise walk thousands of days on every keystroke.
  for (let guard = 0; day <= last && guard < 400; guard += 1) {
    const weekday = day.getUTCDay();
    if (weekday !== 0 && weekday !== 6) count += 1;
    day.setUTCDate(day.getUTCDate() + 1);
  }

  return count;
}

/**
 * The Deduct card's two figures, right-aligned: the balance the days would come off, and what
 * would remain. The headings are the Credit Balance table's own word, `Balance`. Remaining is an
 * em dash until the days are a number. A negative remaining is shown as it is, untinted —
 * whether one is allowed is not decided yet.
 */
function DeductionFigures({
  balance,
  remaining,
  supplementary,
}: {
  balance: number;
  remaining: number | null;
  /** The supplementary type's own pair, once one is picked to cover the shortfall. */
  supplementary?: { name: string; balance: number; remaining: number } | null;
}) {
  const figure = (value: number | null) => (
    <span className="font-mono text-sm tabular-nums text-slate-600">
      {value != null ? value.toFixed(2) : <span className="text-slate-400">—</span>}
    </span>
  );

  return (
    <span className="grid grid-cols-[auto_auto] items-baseline gap-x-3 gap-y-0.5 text-right">
      <span className="text-xs text-slate-500">Balance</span>
      {figure(balance)}
      <span className="text-xs text-slate-500">Remaining balance</span>
      {figure(remaining)}
      {supplementary ? (
        <>
          <span className="mt-1 text-xs text-slate-500">{supplementary.name} balance</span>
          <span className="mt-1">{figure(supplementary.balance)}</span>
          <span className="text-xs text-slate-500">{supplementary.name} remaining</span>
          {figure(supplementary.remaining)}
        </>
      ) : null}
    </span>
  );
}

/** A type can be deducted from when it keeps a running balance — its own, or its parent's. */
function canDeduct(type: LeaveType | null): boolean {
  return type == null || type.creditKind === LeaveCreditKind.Ledger;
}

/**
 * Records, or corrects, one approved leave on the Leave History tab.
 *
 * **Every record is approved**, so the approval is part of the form rather than a later step.
 * **Deduct or record only is asked, never assumed** — `bool?` on the wire, seeded EMPTY on a
 * create; a deducting record writes a Used ledger entry in the same save.
 *
 * Filed by and Approved by are ACCOUNTS — no name is typed, so none is stored.
 */
export function LeaveRecordModal({
  personnelId,
  employeeUserId,
  record,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** The employee's own linked account, if any — the Filed by a create opens on. */
  employeeUserId: string | null;
  /** Null creates. */
  record: LeaveRecord | null;
  onClose: () => void;
  onSaved: (result?: { keepOpen?: boolean }) => void;
}) {
  const { user } = useAuth();
  const editing = record !== null;

  const loadUsers = useCallback(
    (signal: AbortSignal) => leaveRecordApi.userOptions(personnelId, signal),
    [personnelId],
  );
  const accounts = useRecruitmentUsers(loadUsers);

  const [types, setTypes] = useState<LeaveType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);

  const [leaveTypeId, setLeaveTypeId] = useState(record?.leaveTypeId ?? '');
  const [startDate, setStartDate] = useState(record?.startDate ?? '');
  const [endDate, setEndDate] = useState(record?.endDate ?? '');
  const [days, setDays] = useState(record ? String(record.days) : '');
  const [daysTouched, setDaysTouched] = useState(editing);
  const [deduction, setDeduction] = useState<Deduction>(
    record ? (record.deductsCredit ? 'deduct' : 'record') : '',
  );
  const [filedBy, setFiledBy] = useState<string | null>(record ? (record.filedByUserId ?? null) : employeeUserId);
  const [filedDate, setFiledDate] = useState(record?.filedDate ?? '');
  const [reason, setReason] = useState(record?.reason ?? '');
  const [approvedBy, setApprovedBy] = useState<string | null>(
    record ? (record.approvedByUserId ?? null) : (user?.id ?? null),
  );
  const [approvedDate, setApprovedDate] = useState(record?.approvedDate ?? todayCalendarDate());
  const [approvalRemarks, setApprovalRemarks] = useState(record?.approvalRemarks ?? '');
  const [supplementaryId, setSupplementaryId] = useState<string | null>(record?.supplementaryLeaveTypeId ?? null);

  const [addAnother, setAddAnother] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The balances behind the Deduct card's figures. A failed fetch (a writer without
    `leave-credits.read`, say) leaves them null and the card simply shows none.
  */
  const [balances, setBalances] = useState<LeaveCreditBalance[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    leaveCreditApi
      .balances(personnelId, controller.signal)
      .then(setBalances)
      .catch(() => {
        /* No figures; the choice itself still works. */
      });
    return () => controller.abort();
  }, [personnelId]);

  useEffect(() => {
    const controller = new AbortController();
    leaveTypeApi
      .options(controller.signal)
      .then((loaded) => {
        setTypes(loaded);
        setTypesLoading(false);
      })
      .catch(() => {
        /* The picker stays empty; the form reports its own failures on save. */
        setTypesLoading(false);
      });
    return () => controller.abort();
  }, []);

  const chosen = types.find((t) => t.id === leaveTypeId) ?? null;
  const deductible = canDeduct(chosen);

  /*
    WHOSE balance a deduction draws on: the type's own, or — for a child SHARING its parent's
    credit — the parent's. The same rule the server's balance applies.
  */
  const poolOf = (typeId: string): string => {
    const type = types.find((t) => t.id === typeId);
    return type?.sharesParentCredit && type.parentLeaveTypeId != null ? type.parentLeaveTypeId : typeId;
  };

  const pool = chosen ? poolOf(chosen.id) : null;
  const poolBalance = pool ? balances?.find((b) => b.leaveTypeId === pool)?.balance : undefined;

  /*
    AN EDIT already has its own days off the stored balances — the leave type's share and the
    supplementary type's. Correcting it must not count them twice, so each is added back to
    whichever pool it was taken from. The server reads its balance the same way.
  */
  const addedBack = (poolId: string | null): number => {
    if (poolId == null || !record?.deductsCredit || !record.isActive) return 0;
    const supplementaryDays = record.supplementaryDays ?? 0;
    let total = poolOf(record.leaveTypeId) === poolId ? record.days - supplementaryDays : 0;
    if (record.supplementaryLeaveTypeId != null && poolOf(record.supplementaryLeaveTypeId) === poolId) {
      total += supplementaryDays;
    }
    return total;
  };

  const available = poolBalance != null ? poolBalance + addedBack(pool) : null;

  /*
    DERIVED DURING RENDER, never in an effect, and only until the reader types — a suggestion
    that follows the dates, then gets out of the way. Never on an edit: the stored figure is
    what the application said.
  */
  const suggested = weekdaysBetween(startDate, endDate);
  const shownDays = daysTouched ? days : suggested != null && suggested > 0 ? String(suggested) : '';

  const parsedDays = Number(shownDays);
  const span = startDate && endDate && endDate >= startDate
    ? Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000) + 1
    : null;
  const daysValid = shownDays.trim() !== '' && Number.isFinite(parsedDays) && parsedDays > 0
    && (span == null || parsedDays <= span);
  /*
    THE SPLIT, previewed exactly as the server makes it: the leave type gives what it holds, a
    supplementary type takes the rest. The server re-reads the balances when saving, so this is
    what the reader sees, not what the request asserts.
  */
  const deducting = deduction === 'deduct' && deductible;
  const covered = available != null && daysValid ? Math.min(Math.max(available, 0), parsedDays) : null;
  const shortfall = deducting && covered != null ? parsedDays - covered : 0;

  // Only types with a running balance of their OWN can cover the shortfall.
  const supplementaryOptions = types.filter((t) =>
    t.creditKind === LeaveCreditKind.Ledger && pool != null && poolOf(t.id) !== pool);
  const supplementary = shortfall > 0
    ? supplementaryOptions.find((t) => t.id === supplementaryId) ?? null
    : null;

  const supplementaryPool = supplementary ? poolOf(supplementary.id) : null;
  const supplementaryBalance = supplementaryPool != null
    ? remainingBalance(balances, supplementaryPool)
    : null;
  const supplementaryAvailable = supplementaryBalance != null
    ? supplementaryBalance + addedBack(supplementaryPool)
    : null;

  // What the leave type would hold once its share comes off — only when both figures are real.
  const remaining = available != null && daysValid
    ? available - (supplementary ? covered ?? parsedDays : parsedDays)
    : null;

  const datesValid = startDate !== '' && endDate !== '' && endDate >= startDate;
  const filedValid = filedDate === '' || approvedDate === '' || filedDate <= approvedDate;

  const valid =
    leaveTypeId !== ''
    && datesValid
    && daysValid
    && deduction !== ''
    && (deduction !== 'deduct' || deductible)
    && approvedBy !== null
    && approvedDate !== ''
    && filedValid;

  const canSave = valid && confirmed && !saving;

  function clearIdentity() {
    setStartDate('');
    setEndDate('');
    setDays('');
    setDaysTouched(false);
    setFiledDate('');
    setReason('');
    setApprovalRemarks('');
    setConfirmed(false);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      leaveTypeId,
      startDate,
      endDate,
      days: parsedDays,
      deductsCredit: deduction === 'deduct',
      // Sent only while it would be used; the server clears it otherwise anyway.
      supplementaryLeaveTypeId: supplementary?.id ?? null,
      filedByUserId: filedBy,
      filedDate: filedDate || null,
      reason: reason.trim() || null,
      approvedByUserId: approvedBy,
      approvedDate,
      approvalRemarks: approvalRemarks.trim() || null,
    };

    try {
      if (record) await leaveRecordApi.update(personnelId, record.id, request);
      else await leaveRecordApi.create(personnelId, request);

      if (!editing && addAnother) {
        // The type, the people and the deduction choice are the placement; the leave itself goes.
        clearIdentity();
        onSaved({ keepOpen: true });
      } else {
        onSaved();
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this leave record.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'leave-record',
        // The radio group carries its id on each option, so the deduction is named explicitly.
        fieldIds: { deductsCredit: 'leave-record-deducts-credit-deduct' },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleLeaveRecord(types, user?.id ?? accounts.users[0]?.userId ?? null);

    setLeaveTypeId(sample.leaveTypeId);
    setStartDate(sample.startDate);
    setEndDate(sample.endDate);
    setDays(sample.days);
    setDaysTouched(true);
    setDeduction(sample.deductsCredit ? 'deduct' : 'record');
    if (filedBy === null && accounts.users[0]) setFiledBy(accounts.users[0].userId);
    setFiledDate(sample.filedDate);
    setReason(sample.reason);
    setApprovedBy(sample.approvedByUserId);
    setApprovedDate(sample.approvedDate);
    setApprovalRemarks(sample.approvalRemarks);
  }

  const accountOptions = accounts.users.map((u) => ({ value: u.userId, label: u.displayName }));

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit leave record' : 'Record leave'}
      size="2xl"
      id="leave-record-modal"
      autogenerate={{ onGenerate: generate, disabled: saving || typesLoading }}
      addAnother={
        editing
          ? undefined
          : { id: 'leave-record-add-another', checked: addAnother, onChange: setAddAnother, disabled: saving }
      }
      confirm={{
        id: 'leave-record-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="leave-record-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="leave-record-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="leave-record-leave-type-id"
            label="Leave type"
            required
            description="Which kind of leave was taken."
            value={leaveTypeId}
            onChange={(next) => {
              setLeaveTypeId(next);

              // A granted type has no balance to deduct from, so a deduction already chosen clears.
              if (deduction === 'deduct' && !canDeduct(types.find((t) => t.id === next) ?? null)) {
                setDeduction('');
              }
            }}
            loading={typesLoading}
            error={leaveTypeId ? error?.fieldError('leaveTypeId') : 'Choose a leave type.'}
            disabled={saving}
            options={types.map((t) => ({
              value: t.id,
              label: t.name,
              sublabel: t.code ?? undefined,
              // What is left, at the outer right — a sharing child reads its parent's balance.
              right: <LeaveBalanceFigure value={remainingBalance(balances, t.id)} />,
            }))}
          />

          <TextField
            id="leave-record-days"
            label="Days"
            required
            description="Days of leave as the application counts them. Suggested from the weekdays in the period until you type — halves are fine."
            value={shownDays}
            onChange={(e) => {
              setDaysTouched(true);
              setDays(e.target.value);
            }}
            error={
              daysValid
                ? error?.fieldError('days')
                : span != null && parsedDays > span
                  ? 'The days cannot exceed the number of calendar days in the period.'
                  : 'Enter a number of days above zero.'
            }
            disabled={saving}
          />

          <DateField
            id="leave-record-start-date"
            label="From"
            required
            description="The first day of the leave."
            value={startDate}
            onChange={setStartDate}
            error={startDate ? error?.fieldError('startDate') : 'Enter the first day of the leave.'}
            disabled={saving}
          />

          <DateField
            id="leave-record-end-date"
            label="To"
            required
            description="The last day of the leave — the same day as From for a single day."
            value={endDate}
            min={startDate || undefined}
            onChange={setEndDate}
            error={
              !endDate
                ? 'Enter the last day of the leave.'
                : startDate && endDate < startDate
                  ? 'The leave cannot end before it starts.'
                  : error?.fieldError('endDate')
            }
            disabled={saving}
          />
        </div>

        {/*
          Directly under the period (product call, 27 Oct 2026): the reason belongs to the leave
          itself, so it is read with its dates, not after the approval. Full width, outside the
          grid as a sibling — a free note about the leave as a whole.
        */}
        <Textarea
          id="leave-record-reason"
          label="Reason"
          description="Why the leave was taken, in the application's words. Optional."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error?.fieldError('reason')}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="leave-record-filed-by-user-id"
            label="Filed by"
            description={accounts.error ?? 'The account that filed the application. Optional — an old record may not say.'}
            placeholder="Choose an account…"
            value={filedBy ?? ''}
            onChange={(next) => setFiledBy(next || null)}
            options={accountOptions}
            loading={accounts.loading}
            error={error?.fieldError('filedByUserId')}
            disabled={saving}
          />

          <DateField
            id="leave-record-filed-date"
            label="Filed at"
            description="The day the application was filed. Optional."
            value={filedDate}
            max={approvedDate || undefined}
            onChange={setFiledDate}
            error={filedValid ? error?.fieldError('filedDate') : 'The leave cannot be filed after it was approved.'}
            disabled={saving}
          />

          <Select
            id="leave-record-approved-by-user-id"
            label="Approved by"
            required
            description={accounts.error ?? 'The account that approved the leave — not necessarily whoever records it here.'}
            placeholder="Choose an account…"
            value={approvedBy ?? ''}
            onChange={(next) => setApprovedBy(next || null)}
            options={accountOptions}
            loading={accounts.loading}
            error={approvedBy ? error?.fieldError('approvedByUserId') : 'Choose who approved the leave.'}
            disabled={saving}
          />

          <DateField
            id="leave-record-approved-date"
            label="Approved at"
            required
            description="The day the leave was approved."
            value={approvedDate}
            max={todayCalendarDate()}
            onChange={setApprovedDate}
            error={approvedDate ? error?.fieldError('approvedDate') : 'Enter the day the leave was approved.'}
            disabled={saving}
          />
        </div>

        {/* Full width, outside the grid as a sibling: the approver's note about the leave as a whole. */}
        <Textarea
          id="leave-record-approval-remarks"
          label="Approval remarks"
          description="The approver's own remarks. Optional."
          value={approvalRemarks}
          onChange={(e) => setApprovalRemarks(e.target.value)}
          error={error?.fieldError('approvalRemarks')}
          disabled={saving}
        />

        {/*
          LAST, by product call (27 Oct 2026): the deduction is decided once the leave and its
          approval are filled in, beside the figures it would move.
        */}
        <RadioGroup<Deduction>
          id="leave-record-deducts-credit"
          label="Leave credit"
          required
          description="Whether these days come off the employee's leave credit, or the leave is recorded for the history only."
          value={deduction}
          onChange={setDeduction}
          // Each card takes the full row: side by side, every description wrapped to three lines.
          columns={1}
          error={
            deduction === 'deduct' && !deductible
              ? 'A granted leave type keeps no running balance, so there is nothing to deduct from. Record it only.'
              : deduction
                ? error?.fieldError('deductsCredit')
                : 'Choose whether this leave deducts from the leave credit.'
          }
          disabled={saving}
          options={[
            {
              value: 'deduct',
              label: 'Deduct from leave credit',
              description: deductible
                ? 'Records the days as used on this type — the balance goes down.'
                : 'Not available — this type is granted and keeps no running balance.',
              disabled: !deductible,
              aside: deductible && available != null ? (
                <DeductionFigures
                  balance={available}
                  remaining={remaining}
                  supplementary={
                    supplementary && supplementaryAvailable != null
                      ? {
                          name: supplementary.name,
                          balance: supplementaryAvailable,
                          remaining: supplementaryAvailable - shortfall,
                        }
                      : null
                  }
                />
              ) : undefined,
            },
            {
              value: 'record',
              label: 'Record only',
              description: 'Keeps the leave in the history. The balance does not move.',
            },
          ]}
        />

        {/*
          ONLY WHILE THE LEAVE TYPE FALLS SHORT (product call, 27 Oct 2026): five days of Vacation
          on a balance of three asks which type covers the other two. Last in the form, so its
          arrival moves nothing below it. Optional — left empty, the leave type goes below zero.
        */}
        {shortfall > 0 ? (
          <Select
            id="leave-record-supplementary-leave-type-id"
            label="Supplementary leave type"
            description={`${chosen?.name ?? 'The leave type'} covers ${(covered ?? 0).toFixed(2)} of the ${parsedDays.toFixed(2)} days. Choose a type to take the other ${shortfall.toFixed(2)}; left empty, ${chosen?.name ?? 'the leave type'} goes below zero.`}
            placeholder="Choose a leave type…"
            value={supplementary?.id ?? ''}
            onChange={(next) => setSupplementaryId(next || null)}
            error={error?.fieldError('supplementaryLeaveTypeId')}
            disabled={saving}
            options={supplementaryOptions.map((t) => ({
              value: t.id,
              label: t.name,
              sublabel: t.code ?? undefined,
              right: <LeaveBalanceFigure value={remainingBalance(balances, t.id)} />,
            }))}
          />
        ) : null}
      </div>
    </Modal>
  );
}
